import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import ScrollItem__BlockDefault from './ScrollItem__BlockDefault';

// Porcentajes del área de pantalla sobre la foto del device (0–100).
// Escalan con el tamaño: se aplican a offsetWidth/Height actuales en cada update.
const CORNERS = {
    tl: [5.19 * .8, 21.11 * .95],
    tr: [80.83 * 1.01, 10.83 * .9],
    br: [95.74 * 1.01, 70.28 * 1.02],
    bl: [20.46 * .98, 88.33 * 1.022]
};

class ScrollItem__BlockMockup extends ScrollItem__BlockDefault {
    constructor(__link, __index, __scroller) {
        super(__link, __index, __scroller);

        this.wrapper = this.item.querySelector('.tablet-wrapper');
        this.device = this.wrapper?.querySelector(':scope > figure') || this.wrapper;
        this.screen = this.item.querySelector('.screen-video');
        this.content = this.screen?.querySelector('[data-scroll-displace]');
        this.contentImg = this.content?.querySelector('img');
        this.deviceImg = this.device?.querySelector('img');

        this._ro = null;
        this._raf = 0;
        this._lastKey = '';

        if (this.wrapper && typeof ResizeObserver !== 'undefined') {
            this._ro = new ResizeObserver(() => this.scheduleUpdate());
            this._ro.observe(this.wrapper);
            this._ro.observe(this.device);
            if (this.content) this._ro.observe(this.content);
        }

        [this.deviceImg, this.contentImg].forEach((img) => {
            if (img && !img.complete) {
                img.addEventListener('load', () => this.scheduleUpdate(), { once: true });
            }
        });

        this.onShow = () => this.scheduleUpdate(true);

        this.scheduleUpdate(true);
    }

    resize(__w, __h) {
        super.resize(__w, __h);
        this.scheduleUpdate(true);
    }

    scheduleUpdate(force = false) {
        if (this._raf) cancelAnimationFrame(this._raf);

        // Doble rAF: espera a que layout/media-queries asienten tras resize/orientación
        this._raf = requestAnimationFrame(() => {
            this._raf = requestAnimationFrame(() => {
                this._raf = 0;
                this.syncMockup(force);
            });
        });
    }

    // OJO: no llamar `update` — Scroll_Item.update() escribe --progress en cada frame
    syncMockup(force = false) {
        if (!this.wrapper || !this.screen) return;

        const w = this.device.offsetWidth;
        const h = this.device.offsetHeight;
        if (w === 0 || h === 0) return;

        const key = `${w}x${h}`;
        if (!force && key === this._lastKey) {
            this.updateDisplaceOffset(w, h);
            return;
        }
        this._lastKey = key;

        this.updateTransform(w, h);
        this.updateDisplaceOffset(w, h);
    }

    /** Altura natural del content a la anchura actual del screen (escala con el resize). */
    getContentHeight(screenW) {
        const img = this.contentImg;
        if (img) {
            const iw = Number(img.getAttribute('width')) || img.naturalWidth;
            const ih = Number(img.getAttribute('height')) || img.naturalHeight;
            if (iw > 0 && ih > 0) return screenW * (ih / iw);
        }

        if (this.content) {
            const aspect = getComputedStyle(this.content).getPropertyValue('--aspect').trim();
            if (aspect.includes('/')) {
                const [aw, ah] = aspect.split('/').map(Number);
                if (aw > 0 && ah > 0) return screenW * (ah / aw);
            }
            return this.content.offsetHeight;
        }

        return 0;
    }

    updateDisplaceOffset(deviceW, deviceH) {
        if (!this.content || !this.screen) return;

        // Ventana local del screen = caja del device (antes del warp). Escala 1:1 con el resize.
        const screenH = this.screen.clientHeight || deviceH;
        const screenW = this.screen.clientWidth || deviceW;
        if (screenH === 0 || screenW === 0) return;

        const contentH = this.getContentHeight(screenW);
        const offset = Math.max(0, contentH - screenH);

        this.content.style.setProperty('--offset', `${offset}px`);
    }

    updateTransform(w, h) {
        return;
        const src = [[0, 0], [w, 0], [w, h], [0, h]];
        const dst = [
            [CORNERS.tl[0] / 100 * w, CORNERS.tl[1] / 100 * h],
            [CORNERS.tr[0] / 100 * w, CORNERS.tr[1] / 100 * h],
            [CORNERS.br[0] / 100 * w, CORNERS.br[1] / 100 * h],
            [CORNERS.bl[0] / 100 * w, CORNERS.bl[1] / 100 * h]
        ];

        const M = this.getPerspectiveTransform(src, dst);
        if (!M) return;

        this.screen.style.transform = this.matrixToCSS(M);
    }

    solveLinear(A, B) {
        const n = A.length;
        const M = A.map((row, i) => [...row, B[i]]);

        for (let i = 0; i < n; i++) {
            let maxRow = i;
            for (let k = i + 1; k < n; k++) {
                if (Math.abs(M[k][i]) > Math.abs(M[maxRow][i])) maxRow = k;
            }
            [M[i], M[maxRow]] = [M[maxRow], M[i]];

            if (Math.abs(M[i][i]) < 1e-10) return null;

            for (let k = i + 1; k < n; k++) {
                const factor = M[k][i] / M[i][i];
                for (let j = i; j <= n; j++) {
                    M[k][j] -= factor * M[i][j];
                }
            }
        }

        const x = new Array(n).fill(0);
        for (let i = n - 1; i >= 0; i--) {
            let sum = M[i][n];
            for (let j = i + 1; j < n; j++) {
                sum -= M[i][j] * x[j];
            }
            x[i] = sum / M[i][i];
        }
        return x;
    }

    getPerspectiveTransform(src, dst) {
        const A = [];
        const B = [];

        for (let i = 0; i < 4; i++) {
            const [x, y] = src[i];
            const [u, v] = dst[i];
            A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
            B.push(u);
            A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
            B.push(v);
        }

        const sol = this.solveLinear(A, B);
        if (!sol || sol.some((n) => !Number.isFinite(n))) return null;

        const [a, b, c, d, e, f, g, h] = sol;

        return [
            [a, b, c],
            [d, e, f],
            [g, h, 1]
        ];
    }

    matrixToCSS(M) {
        const [[a, b, c], [d, e, f], [g, h, i]] = M;
        return `matrix3d(
            ${a}, ${d}, 0, ${g},
            ${b}, ${e}, 0, ${h},
            0, 0, 1, 0,
            ${c}, ${f}, 0, ${i}
        )`;
    }

    dispose() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._ro?.disconnect();
        this._ro = null;
        super.dispose();
    }
}

Scroll._registerClass('BlockMockup', ScrollItem__BlockMockup);

export default ScrollItem__BlockMockup;
